import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  MusicalNoteIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  AudioContent,
  AudioContentData,
  AudioFilters,
  AudioStream,
  VariantExtraTrack,
  VariantSlot
} from '../types/audio';
import { HeadphonesRecommendation } from '../types';
import { AudioForm } from '../components/forms/AudioForm';
import { AudioFormData } from '../types/audio';
import { audioService } from '../services/audioService';
import { ProductType, ProductTypeLabels, audioProductTypes } from '../types/productType';
import { useHlsStatusMap, HlsStatus } from '../lib/useHlsStatusMap';
import { getErrorDetails, getErrorMessage } from '../utils/errors';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PageLayout } from '../components/ui/PageLayout';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useRueckfrage } from '../hooks/useRueckfrage';
import { useFassungen } from '../hooks/useFassungen';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { produkttypAuswahl, seitenwechsel, statusAuswahl, zugriffAuswahl } from '../components/ui/filterZustand';

// Alt-Bestände könnten `productCategory`/`coverImage` noch in der contentData-Spalte tragen. Der
// heutige Backend-Writer tut das nicht mehr (audioMapping.ts schreibt dort nur `audioBundles` und
// `variantTracks`), beweisbar tot ist es damit aber nicht — die Lesezweige bleiben deshalb erhalten
// und werden hier nur typisiert statt über `as any` gelesen.
type LegacyContentData = AudioContentData & { productCategory?: string; coverImage?: string };
const legacyContentData = (audio: AudioContent): LegacyContentData | undefined => audio.contentData;

// Ein Stream aus Alt-Beständen kann flach vorliegen (mp3Url/flacUrl/url) statt als mp3/flac-Objekt.
// Diese Lese-Form deckt beide Formen ab, damit die vorhandenen Fallbacks unverändert greifen.
type LooseAudioStream = Partial<AudioStream> & { mp3Url?: string; flacUrl?: string; url?: string };
type LooseVariantTrack = Partial<VariantExtraTrack> & { mp3?: { url?: string }; url?: string };

const mapAudioContentToFormData = (audio: AudioContent): Partial<AudioFormData> => {
  // normalizeAudioContent (audioService) spiegelt contentData.streams bereits nach audio.streams —
  // der zweite Lesezweig auf contentData.streams war deshalb nicht mehr erreichbar.
  const streams: Partial<Record<VariantSlot, LooseAudioStream>> = audio.streams || {};
  const getStream = (key: VariantSlot): LooseAudioStream => streams[key] || {};
  const getStreamUrl = (streamKey: VariantSlot, format: 'mp3' | 'flac') => {
    const stream = getStream(streamKey);
    if (format === 'mp3') {
      return stream.mp3?.url || stream.mp3Url || stream.url || '';
    }
    return stream.flac?.url || stream.flacUrl || '';
  };

  const affirmations = audio.metadata?.affirmations;
  const frequencies = audio.metadata?.frequencies;

  return {
    title: audio.title,
    description: audio.description,
    // shortDescription/longDescription/headphones/topicCategories gibt es NICHT auf oberster Ebene
    // der API-Antwort (keine content-Spalte, keine Backend-Anreicherung) — nur in metadata.
    shortDescription: audio.metadata?.shortDescription,
    duration: audio.duration || audio.metadata?.duration,
    headphones: audio.metadata?.headphones as HeadphonesRecommendation | undefined,
    // Laufzeit-Slugs aus der Datenbank — kein Enum-Cast mehr (siehe PR #623).
    topicCategories: (audio.metadata?.topicCategories as string[] | undefined) || [],
    productCategory:
      audio.productCategory
      || ((audio.metadata?.productCategory || legacyContentData(audio)?.productCategory) as ProductType | undefined),
    silentMp3Url: getStreamUrl('silent', 'mp3'),
    silentFlacUrl: getStreamUrl('silent', 'flac'),
    silentVolume: getStream('silent')?.volume ?? 100,
    silentEnabled: getStream('silent')?.enabled ?? false,
    audioMp3Url: getStreamUrl('audio', 'mp3'),
    audioVolume: getStream('audio')?.volume ?? 100,
    audioEnabled: getStream('audio')?.enabled ?? false,
    frequenzMp3Url: getStreamUrl('frequenz', 'mp3'),
    frequenzVolume: getStream('frequenz')?.volume ?? 100,
    frequenzEnabled: getStream('frequenz')?.enabled ?? false,
    natureMp3Url: getStreamUrl('nature', 'mp3'),
    natureVolume: getStream('nature')?.volume ?? 100,
    natureEnabled: getStream('nature')?.enabled ?? false,
    // Kopfhörer-Empfehlung pro Variante aus metadata.headphonesByVariant in die Flach-Felder
    // laden, damit die Dropdowns die bestehenden Werte zeigen (sonst überschreibt ein Save sie).
    audioHeadphones: audio.metadata?.headphonesByVariant?.audio || '',
    silentHeadphones: audio.metadata?.headphonesByVariant?.silent || '',
    frequenzHeadphones: audio.metadata?.headphonesByVariant?.frequenz || '',
    natureHeadphones: audio.metadata?.headphonesByVariant?.nature || '',
    // Affirmationen (string[]) → Textarea-String (eine pro Zeile) fürs Formular.
    affirmations: Array.isArray(affirmations) ? affirmations.join('\n') : '',
    // Frequenzen (string[]) → Textarea-String (eine pro Zeile).
    frequencies: Array.isArray(frequencies) ? frequencies.join('\n') : '',
    // Zusätzliche Varianten-Spuren (position ≥1) aus dem persistierten contentData.variantTracks laden
    // (gruppiert { slot: [{title,mp3Url,enabled}] }) → flache UI-Liste. Round-trippt exakt mit dem,
    // was das Formular sendet (audioService gruppiert wieder). Quelle ist die CMS-Autoring-Eingabe.
    variantTracks: (() => {
      // Ein Top-Level-`variantTracks` liefert die API nicht — die Gruppierung steht in contentData.
      const grouped: Partial<Record<VariantSlot, LooseVariantTrack[]>> | undefined =
        audio.contentData?.variantTracks;
      if (!grouped || typeof grouped !== 'object') return [];
      const out: VariantExtraTrack[] = [];
      (['audio', 'silent', 'frequenz', 'nature'] as const).forEach((slot) => {
        const arr = grouped[slot];
        if (Array.isArray(arr)) {
          arr.forEach((t) => {
            out.push({
              slot,
              title: t?.title || '',
              mp3Url: t?.mp3Url || t?.mp3?.url || t?.url || '',
              enabled: t?.enabled !== false,
            });
          });
        }
      });
      return out;
    })(),
    artist: audio.metadata?.artist,
    // Kuratierter Interpret: FK-Spalte content.artistId (Fallback artistRef.id) → Dropdown pre-select.
    artistId: audio.artistId ?? audio.artistRef?.id ?? '',
    // Album = die kanonische content.album-Spalte (das liest auch die Playlist/das Album-Feature;
    // Backend dual-writet sie mit metadata.album). Fallback auf metadata.album nur für Alt-Rows,
    // deren content.album (noch) leer ist. So zeigt das CMS exakt das, was die Playlist zeigt.
    album: audio.album ?? audio.metadata?.album,
    language: audio.metadata?.language,
    // Ein Top-Level-`coverImage` gibt es nicht — die Cover-URL steht in metadata.coverImage.
    coverImageUrl: audio.metadata?.coverImage || '',
    isPremium: audio.isPremium,
    tags: audio.tags,
    // Bestehenden Status laden, damit ein Save ihn erhält (statt auf DRAFT zurückzufallen).
    status: audio.status,
  };
};

const AudioManagement: React.FC = () => {
  const [audioContent, setAudioContent] = useState<AudioContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAudio, setEditingAudio] = useState<AudioContent | null>(null);
  // ⚠️ Hier und nicht in den zwei Flächen darunter: Formular (Datei-Deckung je Schalter) und
  // `Fassungsliste` brauchen DIESELBEN Daten. Zwei Abrufe wären Zugabe, zwei Zustände ein
  // Widerspruch auf einem Bildschirm. `null` beim Anlegen — dann ruft der Haken nichts ab.
  const fassungen = useFassungen(editingAudio?.id ?? null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [filters, setFilters] = useState<AudioFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    isPremium: undefined,
    category: undefined,
  });
  // Live-HLS-Status (audio_tracks via Supabase Realtime), gekeyed nach Titel.
  const hlsStatusMap = useHlsStatusMap();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  // Zustand, Zeitmessung und Aufräumen des Balkens stecken im Haken. Der Fehler bleibt jetzt
  // stehen, statt nach drei Sekunden zu verschwinden — Begründung in `Benachrichtigung.tsx`.
  //
  // ⚠️ EINE Quelle für diesen Bildschirm. `melde` geht als `notify` auch ins Formular (wie bei
  // Alben, Interpreten und Playlists), damit die Fehler des Vorhör-Players in denselben Balken
  // laufen — vorher hielt `AudioForm` dafür einen zweiten Zustand.
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  // ⚠️ Das Feld heißt `rückfrage`, nicht `inhalt` — der Name daneben ist schon vergeben (hier sogar
  // zweimal umbenannt). Der Haken ist genau dafür so benannt, siehe `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Memoisiert, damit der Effekt sie in seine Abhängigkeiten nehmen kann. Die Identität wechselt
  // genau dann, wenn sich `filters` ändert — `melde` ist über `useCallback` stabil.
  const fetchAudioContent = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('🔍 AudioManagement - Lade Audio-Inhalte mit Filtern:', filters);
      const response = await audioService.getAudioContent(filters);
      console.log('✅ AudioManagement - Audio-Inhalte geladen:', {
        count: response.data?.length || 0,
        pagination: response.pagination
      });
      setAudioContent(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
    } catch (error: unknown) {
      console.error('❌ Failed to fetch audio content:', error);
      console.error('❌ Error details:', getErrorDetails(error));
      setAudioContent([]);
      melde('error', getErrorMessage(error, 'Fehler beim Laden der Audio-Inhalte'));
    } finally {
      setIsLoading(false);
    }
  }, [filters, melde]);

  useEffect(() => {
    fetchAudioContent();
  }, [fetchAudioContent]);

  const handleFilterChange = <K extends keyof AudioFilters>(key: K, value: AudioFilters[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleCreateAudio = () => {
    setEditingAudio(null);
    setIsFormLoading(false);
    setShowForm(true);
  };

  const handleEditAudio = async (audio: AudioContent) => {
    setShowForm(true);
    setIsFormLoading(true);
    setEditingAudio(null);
    try {
      const fullAudio = await audioService.getAudioContentById(audio.id);
      setEditingAudio(fullAudio);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, 'Unbekannter Fehler beim Laden');
      melde('error', `Fehler beim Laden des Audio-Inhalts: ${errorMessage}`);
      setShowForm(false);
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleDeleteAudio = async (id: string) => {
    // Titel VOR der Frage lesen; gelöscht wird über die übergebene `id`. Damit kann zwischen Frage
    // und Ausführung nichts veralten — `editingAudio` darf sich ändern, das Ziel steht seit dem
    // Klick fest.
    const titel = (editingAudio?.id === id ? editingAudio : audioContent.find((a) => a.id === id))?.title;
    // `gefahr`, unstrittig: das Backend fährt `tx.content.delete()` — ein echtes DELETE, kein
    // Statuswechsel (`AudioService.deleteAudioContent`). Die Fassungen liegen als Spuren am selben
    // Inhalt und hängen per `onDelete: Cascade` daran; an den Spuren wiederum die Playlist-Einträge.
    const bestätigt = await frage({
      titel: 'Audio endgültig löschen?',
      text:
        `${titel ? `„${titel}"` : 'Dieser Audio-Inhalt'} wird aus der Datenbank entfernt — nicht ` +
        'bloß auf Entwurf gesetzt und nicht archiviert.\n\n' +
        'Mit ihm verschwinden alle seine Fassungen und alle Verweise der Nutzerinnen und Nutzer ' +
        'darauf: Favoriten und Playlist-Einträge. Über diese Oberfläche ist das nicht rückgängig ' +
        'zu machen; nur die hochgeladenen Dateien bleiben im Medienspeicher liegen.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await audioService.deleteAudioContent(id);
      melde('success', 'Audio-Inhalt erfolgreich gelöscht');
      // Form schließen, falls der Löschen-Button aus der Detail-Ansicht kam
      // (no-op, wenn ohnehin die Liste sichtbar war).
      setShowForm(false);
      setEditingAudio(null);
      fetchAudioContent();
    } catch (error) {
      console.error('Failed to delete audio content:', error);
      melde('error', 'Fehler beim Löschen des Audio-Inhalts');
    }
  };

  const handleFormSubmit = async (formData: AudioFormData) => {
    try {
      console.log('Submitting audio form data:', formData);
      
      if (editingAudio) {
        const result = await audioService.updateAudioContent(editingAudio.id, formData);
        console.log('Audio updated successfully:', result);
        melde('success', 'Audio-Inhalt erfolgreich aktualisiert');
      } else {
        const result = await audioService.createAudioContent(formData);
        console.log('Audio created successfully:', result);
        melde('success', 'Audio-Inhalt erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingAudio(null);
      fetchAudioContent();
    } catch (error: unknown) {
      console.error('Failed to save audio content:', error);
      console.error('Error details:', getErrorDetails(error));

      const errorMessage = getErrorMessage(error, 'Unbekannter Fehler beim Speichern');


      melde('error', `Fehler beim ${editingAudio ? 'Aktualisieren' : 'Erstellen'} des Audio-Inhalts: ${errorMessage}`);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAudio(null);
  };


  const getHlsBadge = (status?: HlsStatus) => {
    switch (status) {
      case 'READY':
        return <span className="badge badge-success">Bereit</span>;
      case 'PENDING':
        return <span className="badge badge-warning">In Bearbeitung</span>;
      case 'FAILED':
        return <span className="badge badge-error">Fehlgeschlagen</span>;
      default:
        return <span className="text-sm text-secondary-400">–</span>;
    }
  };

  const getCoverUrl = (audio: AudioContent) => {
    // `coverImage`/`coverImageUrl` gibt es auf oberster Ebene nicht (keine content-Spalte, keine
    // Anreicherung); der contentData-Zweig bleibt für mögliche Alt-Bestände erhalten.
    return audio.metadata?.coverImage || legacyContentData(audio)?.coverImage || '';
  };

  // Überschrift der Seitenvorlage. Hing vorher an drei getrennten `return`-Anweisungen; jetzt an
  // einer Stelle, damit es unter dem Balken nur noch EINE Weiche gibt.
  const seitenTitel = () => {
    if (!showForm) return 'Audio-Verwaltung';
    if (isFormLoading) return 'Audio laden...';
    return editingAudio ? 'Audio bearbeiten' : 'Neues Audio erstellen';
  };

  const formularZweig = () => {
    // DEBUG: Logge die editingAudio-Daten für Debugging
    if (editingAudio) {
      console.log('🔍 AudioManagement - editingAudio Daten:', {
        id: editingAudio.id,
        title: editingAudio.title,
        hasStreams: !!editingAudio.streams,
        hasContentData: !!editingAudio.contentData,
        hasContentDataStreams: !!editingAudio.contentData?.streams,
        streamsKeys: editingAudio.streams ? Object.keys(editingAudio.streams) : [],
        contentDataStreamsKeys: editingAudio.contentData?.streams ? Object.keys(editingAudio.contentData.streams) : [],
        silentMp3Url: editingAudio.streams?.silent?.mp3?.url || editingAudio.contentData?.streams?.silent?.mp3?.url,
        audioMp3Url: editingAudio.streams?.audio?.mp3?.url || editingAudio.contentData?.streams?.audio?.mp3?.url,
      });
    }

    if (isFormLoading) {
      return (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Audio-Inhalt...</p>
        </div>
      );
    }

    const initialData: Partial<AudioFormData> = editingAudio
      ? mapAudioContentToFormData(editingAudio)
      : {};

    return (
      <>
        <AudioForm
          key={editingAudio?.id || 'new-audio'}
          // Die eingelesenen Fassungen: EINMAL geladen (oben, `useFassungen`), von Formular und
          // Liste gelesen. Begründung für die eine Quelle steht im Kopf des Hakens.
          fassungen={fassungen}
          initialData={initialData}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isFormLoading}
          audioId={editingAudio?.id}
          onDelete={handleDeleteAudio}
          // Das Formular hält KEINEN eigenen Meldungs-Zustand mehr; seine Vorhör-Fehler laufen in
          // denselben Balken wie das Speichern. Begründung an der Eigenschaft in `AudioForm.tsx`.
          notify={melde}
        />

        {/* ⚠️ Hier stand die eigenständige `Fassungsliste`. Sie ist NICHT gelöscht, sondern
            AUFGEGANGEN: ihre Zeilen sind seit dem 2026-08-31 die Fassungs-Zeilen der Sektion
            „Audio-Fassungen" im Formular, ihre Nicht-Ton-Dateien stehen dort unter „Weitere
            Dateien dieses Werks", ihr Werkordner in der Fußzeile derselben Sektion. Grund ist
            Toms Satz: „Ich will EINE Fläche sehen, nicht drei." Eine Liste, die dasselbe ein
            zweites Mal zeigt, wäre genau die dritte Fläche, die verschwinden sollte.
            Der Abruf bleibt hier oben (`useFassungen`) — er versorgt jetzt nur noch einen
            Verbraucher, aber die Seite ist weiterhin sein Besitzer. */}
      </>
    );
  };

  const listenZweig = () => (
    <>
      {/* Die drei Feld-Definitionen stehen in `filterFelder.ts` — sie standen hier, in der
          Video- und in der MindBook-Verwaltung buchstabengleich. Keine `as`-Zusicherung mehr:
          `statusAuswahl` liefert `ContentStatus | undefined`, den Typ von `AudioFilters.status`. */}
      <Filterleiste
        suche={filters.search || ''}
        onSuche={(wert) => handleFilterChange('search', wert)}
        platzhalter="Titel, Künstler, Album..."
        auswahlen={[
          produkttypAuswahl(audioProductTypes, filters.category, (wert) => handleFilterChange('category', wert)),
          statusAuswahl(filters.status, (wert) => handleFilterChange('status', wert)),
          zugriffAuswahl(filters.isPremium, (wert) => handleFilterChange('isPremium', wert)),
        ]}
      />

      {/* Content List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Audio-Inhalte...</p>
        </div>
      ) : audioContent.length === 0 ? (
        <div className="text-center py-12 card">
          <MusicalNoteIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Audio-Inhalte gefunden</p>
          <button
            onClick={handleCreateAudio}
            className="mt-4 btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Erstes Audio erstellen
          </button>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock. */}
          <table className="table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Titel</th>
                <th>Künstler</th>
                <th>Produkttyp</th>
                <th>Status</th>
                <th>Zugriff</th>
                <th>HLS</th>
              </tr>
            </thead>
            <tbody>
              {audioContent.map((audio) => {
                const productCategory = audio.productCategory
                  || audio.metadata?.productCategory
                  || legacyContentData(audio)?.productCategory;
                const productTypeLabel = productCategory
                  ? (ProductTypeLabels[productCategory as ProductType] || productCategory)
                  : '–';
                const hlsEntry = hlsStatusMap.get(audio.title);
                return (
                <tr key={audio.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleEditAudio(audio)}
                      className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                      title="Bearbeiten"
                    >
                      {getCoverUrl(audio) ? (
                        <img
                          src={getCoverUrl(audio)}
                          alt={audio.title}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                          <PhotoIcon className="w-5 h-5 text-secondary-400" />
                        </div>
                      )}
                    </button>
                  </td>
                  <td>
                    <div>
                      <button
                        type="button"
                        onClick={() => handleEditAudio(audio)}
                        className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                        title="Bearbeiten"
                      >
                        {audio.title}
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm text-secondary-900">{audio.metadata?.artist || '-'}</div>
                  </td>
                  <td>
                    <div className="text-sm text-secondary-900">{productTypeLabel}</div>
                  </td>
                  <td>
                    <StatusBadge status={audio.status} />
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      {audio.isPremium
                        ? <span className="badge badge-primary">Premium</span>
                        : <span className="badge badge-secondary">Free</span>}
                    </div>
                  </td>
                  <td>
                    <div title={hlsEntry?.status === 'FAILED' ? hlsEntry.error || undefined : undefined}>
                      {getHlsBadge(hlsEntry?.status)}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <SeitenblaetterFuss
            seite={pagination.page}
            seitenAnzahl={pagination.totalPages}
            gesamt={pagination.total}
            onSeitenwechsel={seitenwechsel(setFilters)}
          />
        </div>
      )}
    </>
  );

  return (
    <PageLayout
      title={seitenTitel()}
      actions={
        showForm ? undefined : (
          <button
            onClick={handleCreateAudio}
            className="btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Neues Audio
          </button>
        )
      }
    >
      {/* ⚠️ EIN Balken, und zwar VOR der Weiche — das ist der Fix, nicht die Kosmetik daran.
          Vorher endete `if (showForm)` in einem eigenen `return`, und der Balken stand erst
          dahinter, also nur im Listen-Zweig. `handleFormSubmit` meldet aber im `catch` und lässt
          das Formular bewusst OFFEN, damit die Eingaben nicht verloren gehen: die Fehlermeldung
          ging damit an einen Balken, der in genau diesem Zustand gar nicht im Baum stand. Die
          Nutzerin klickte „Audio speichern", der Server lehnte ab, und sichtbar passierte nichts.
          Mit der Weiche UNTER dem Balken ist dieser Zustand nicht mehr baubar — dieselbe Klasse
          Fehler ist in dieser Schiene schon bei Themen-Kategorien, Mediathek und Alben/Playlists
          aufgetreten.

          `schwebend` nur im Formular-Zweig — belegt, nicht Geschmack, und aus den DEKLARIERTEN
          Werten des gebauten Stylesheets gerechnet:
          • `AudioForm` hat eine eigene festgestellte Knopfleiste (`fixed bottom-0 right-0 left-64
            z-30`) und öffnet den Datei-Wähler (`.bunny-file-picker-overlay`: `fixed inset-0 z-50`
            in `index.css`). Im Textfluss stünde der Balken hinter dem Wähler-Schleier und
            außerhalb des Sichtfelds — genau der Fall, den `schwebend` in `Benachrichtigung.tsx`
            begründet.
          • `schwebend` bringt `fixed top-4 right-4 z-[60]` mit: 60 schlägt Wähler (50) und
            Knopfleiste (30) erklärt statt über die Dokumentreihenfolge. Der Balken steht jetzt VOR
            beiden im Baum, bei Gleichstand verlöre er also — deshalb ist der Abstand zu `z-50`
            nicht optional. Alle drei liegen im Wurzel-Stapelkontext: auf dem Weg dorthin
            (`.page-content`, `.main-content`, `space-y-6`) steht kein `transform`, `filter` oder
            `opacity`, das einen eigenen Kontext aufmachen würde.
          • OBEN rechts, nicht unten: unten rechts klebt die Knopfleiste des Formulars an derselben
            Kante, dort verdeckte der Vorgänger-Balken „Audio speichern". Ausgelöst wird die Meldung
            ohnehin mitten im Formular (Abspiel-Knöpfe) oder von der Knopfleiste selbst.
          • Der Listen-Zweig hat weder Overlay noch festgestellte Leiste und bleibt deshalb bei der
            Mehrheitsfassung im Textfluss.

          ⚠️ Was diese Rechnung NICHT ist: eine Trefferprobe im laufenden Browser. Unter Vitest ist
          sie gar nicht messbar — jsdom hat kein Layout, `getBoundingClientRect` meldet Nullen. Die
          Tests nageln deshalb die deklarierten Stapel- und Positionswerte fest, nicht Pixel. */}
      <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} schwebend={showForm} />

      {/* Die Rückfrage steht ÜBER der Weiche, genau wie der Balken darüber — und der Grund ist
          derselbe.

          ⚠️ Gemessen, nicht von der Nachbarseite übernommen: gefragt wird hier heute
          ausschließlich aus dem FORMULAR-Zweig. `handleDeleteAudio` hat genau einen Aufrufer,
          nämlich die `onDelete`-Eigenschaft von `AudioForm` (Knopf „Audio löschen" in der
          festgestellten Leiste); die Liste hat keine Löschen-Spalte. Trotzdem gehört der Dialog
          NICHT in `formularZweig()`:
          • `formularZweig()` hat selbst ZWEI Rückgaben — im Ladezustand kommt der Spinner zurück
            und `AudioForm` gar nicht. Ein Dialog daneben stünde in genau einer davon.
          • Der Erfolgspfad ruft `setShowForm(false)`: der Zweig, in dem gefragt wurde, verschwindet
            als Folge der Antwort.
          • Bekäme die Liste morgen einen Löschen-Knopf, wäre die Frage aus dem Listen-Zweig
            unsichtbar — dieselbe Fehlerklasse, die den Balken hierher gebracht hat.

          Stapelordnung: der Dialog-Schleier liegt mit `z-[55]` über dem Datei-Wähler des Formulars
          (`z-50`) und unter dem schwebenden Balken (`z-[60]`). Im Browser nachgemessen ist das an
          der Themen-Seite, die als einzige alle drei Schichten gleichzeitig zeigen kann. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {showForm ? formularZweig() : listenZweig()}
    </PageLayout>
  );
};

export default AudioManagement;
